import { ChangeEvent, Dispatch, SetStateAction } from 'react';

import { ArchivalUnitCopy, FileRef } from 'types';

import { useStyles } from './struct-map-styles';

export interface StructMapData {
  id?: string;
  aisId?: string;
  label?: string;
  type?: string;
  subDivisions?: StructMapData[];
  admIds?: string[];
  fileId?: string;
  fileRef?: FileRef;
  physicalFile?: any;
  exportBatchFile?: any;
}

export enum StructMapType {
  DEFAULT = 'DEFAULT',
  ARCHIVAL_UNIT_DEFAULT = 'ARCHIVAL_UNIT_DEFAULT',
  ARCHIVAL_AID_DEFAULT = 'ARCHIVAL_AID_DEFAULT',
  INTERNAL_CHANGE_DEFAULT = 'INTERNAL_CHANGE_DEFAULT',
  EXTERNAL_CHANGE_DEFAULT = 'EXTERNAL_CHANGE_DEFAULT',
  NAD_SHEET_DEFAULT = 'NAD_SHEET_DEFAULT',
}

export type OnLeafClickFn = (props: {
  node: StructMapData;
  parent?: StructMapData;
}) => void;

export interface StructMapProps {
  data: StructMapData;

  onChange: (value: StructMapData) => void;

  onLeafClick?: OnLeafClickFn;

  type?: StructMapType;

  BulkActions?: React.ElementType<{
    allData: StructMapData;
    classes: ReturnType<typeof useStyles>;
    selected: string[];
    specialSelected: string[];
  }>;

  nodeLabelMapper?: (
    node: StructMapData,
    parentEntity: ArchivalUnitCopy,
    isOpen: boolean
  ) => JSX.Element | string;

  nodeTooltipMapper?: (node: StructMapData) => JSX.Element | undefined;

  NodeActions?: React.ElementType<{ node?: StructMapData }>;

  leafLabelMapper?: (leaf: StructMapData) => JSX.Element | string;

  leafTooltipMapper?: (node: StructMapData) => JSX.Element | undefined;

  LeafActions?: React.ElementType<{
    leaf?: StructMapData;
    classes: ReturnType<typeof useStyles>;
  }>;

  defaultExpandedAll?: boolean;

  outterCtxProps?: Partial<StructMapCtxProps>;

  allowSelect?: boolean;

  allowSpecialSelect?: boolean;

  allowDrag?: boolean;

  allowDrop?: boolean;

  allowAdd?: boolean;

  allowEdit?: boolean;

  allowDelete?: boolean;

  missingNodes?: string[];

  extraNodes?: string[];

  parentEntity: ArchivalUnitCopy;
}

export interface StructMapCtxProps {
  allData: StructMapData;

  onChange: (value: StructMapData) => void;

  onLeafClick?: OnLeafClickFn;

  expandedNodes: string[];

  allowSelect: boolean;

  allowSpecialSelect: boolean;

  allowDrag: boolean;

  allowDrop: boolean;

  allowAdd: boolean;

  allowEdit: boolean;

  allowDelete: boolean;

  selectedNodes: string[];

  setSelectedNodes: Dispatch<SetStateAction<string[]>>;

  specialSelectedNodes: string[];

  setSpecialSelectedNodes: Dispatch<SetStateAction<string[]>>;

  editingNodes: string[];

  setEditingNodes: Dispatch<SetStateAction<string[]>>;

  classes: ReturnType<typeof useStyles>;

  type: StructMapType;

  nodeLabelMapper: (
    node: StructMapData,
    parentEntity: ArchivalUnitCopy,
    isOpen: boolean
  ) => JSX.Element | string;

  nodeTooltipMapper: (node: StructMapData) => JSX.Element | undefined;

  NodeActions: React.ElementType<{ node?: StructMapData }>;

  leafLabelMapper: (leaf: StructMapData) => JSX.Element | string;

  leafTooltipMapper: (node: StructMapData) => JSX.Element | undefined;

  LeafActions: React.ElementType<{
    leaf?: StructMapData;
    classes: ReturnType<typeof useStyles>;
  }>;

  draggedNodes: StructMapData[];

  setDraggedNodes: Dispatch<SetStateAction<StructMapData[]>>;

  missingNodes: string[];

  extraNodes: string[];

  handleExpandAll: () => void;

  handleCollapseAll: () => void;

  handleToggle: (event: ChangeEvent<{}>, nodeIds: string[]) => void;

  parentEntity: ArchivalUnitCopy;
}

export interface StructMapItemProps {
  data: StructMapData;

  parent?: StructMapData;
  label: string | JSX.Element;
}
